import * as React from "react";

import NumberFormat, { NumberFormatValues } from "react-number-format";
import { formatAmount, toFixedNumber } from "utils";

import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import { Loader } from "components";
import { TEXT } from "texts";
import { TextFieldProps } from "@material-ui/core/TextField";
import { observer } from "mobx-react-lite";
import { useStores } from "hooks";
import { useStyles } from "./AmountInput.styles";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

type AmountInputProps = {} & TextFieldProps;
const proposedSums = [50, 100, 200];

const NumberFormatCustom = observer(
  (props: { inputRef?: (el: HTMLInputElement) => void; onChange?(value: unknown): void }) => {
    const { inputRef, onChange, ...other } = props;
    const { dataStore } = useStores();
    const handleValue = (values: NumberFormatValues) => {
      return values.value.length < 8;
    };

    return (
      <NumberFormat
        {...other}
        getInputRef={inputRef && inputRef}
        onValueChange={(values) => {
          onChange &&
          onChange({
            currentTarget: {
              value: values.value
            }
          });
        }}
        suffix={` ${dataStore.currency}`}
        allowNegative={false}
        isNumericString
        thousandSeparator={" "}
        decimalSeparator={","}
        decimalScale={2}
        isAllowed={handleValue}
      />
    );
  }
);

const AmountInput: React.FC<AmountInputProps> = ({ disabled }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { dataStore, formStore } = useStores();

  React.useEffect(() => {
    if (dataStore.selectedParticipant) {
      formStore.amount.setValue(dataStore.selectedParticipant.amount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getValue = () => {
    if (!!formStore.amount.value) {
      return formStore.amount.value / 100;
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    formStore.amount.setValue(toFixedNumber(Number(value) * 100));
  };

  const handleCustomAmount = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { value } = event.currentTarget;
    formStore.amount.setValue(toFixedNumber(Number(value) * 100));
  };

  const handleBlur = () => {
    formStore.amount.validate();
  };

  const renderHelperText = () => {
    if (formStore.commission.isLoading) return <Loader type="circular" size={12} />;

    if (formStore.amount.error) {
      return t(formStore.amount.error, {
        min: formatAmount({ amount: formStore.amount.min }),
        max: formatAmount({ amount: formStore.amount.max }),
        currency: dataStore.currency
      });
    }

    if (!formStore.cardNumber.isValid() && !formStore.commission.isValid() && (!formStore.applePayMerchantId || !formStore.googlePaymentData?.googlePayMerchantId)) {
      return " ";
    }

    if (formStore.amount.min && formStore.amount.max && !formStore.commission.value) {
      return `${t(TEXT.from)} ${formStore.amount.min / 100} ${t(TEXT.to)} ${formStore.amount.max / 100} ${dataStore.currency}`;
    }

    if (formStore.commission.isValid()) {
      return `${t(TEXT.commission)} ${formatAmount({ amount: formStore.commission.value })} ${dataStore.currency}`;
    }

    return " ";
  };

  return (
    <FormControl fullWidth>
      <InputLabel htmlFor="amount" shrink className={styles.label} />
      <Input
        autoComplete="off"
        id="amount"
        name="amount"
        placeholder={`0 ${dataStore.currency}`}
        value={getValue()}
        onChange={handleChange}
        onBlur={handleBlur}
        type="tel"
        inputProps={{ className: styles.input, inputMode: "decimal" }}
        inputComponent={NumberFormatCustom}
        disabled={disabled}
        disableUnderline
        onClick={(event) => {
          window.setTimeout(function() {
            (event.target as HTMLInputElement).focus();
          }, 0);
        }}
      />
      <FormHelperText id="amount-helper-text" className={styles.help} component="div" error={!!formStore.amount.error}>
        {renderHelperText()}
      </FormHelperText>

      {!dataStore.hasParticipants &&
        <Box display="flex" justifyContent="center" className={styles.proposedSumWrap}>
          {proposedSums.map(sum => {
            const selected = sum === getValue();

            return (
              <Button
                key={sum}
                value={sum}
                className={clsx(styles.proposedSum, selected && styles.proposedSumFilled)}
                onClick={handleCustomAmount}
              >
                {sum} {dataStore.currency}
              </Button>
            );
          })}
        </Box>
      }
    </FormControl>
  );
};

export default observer(AmountInput);
